import { useCallback, useRef, useState } from "react";
import { preparePcmTranscriptionSegment } from "./audioSegments";
import { mixTabAndMicrophone } from "./mixAudioStreams";
import {
  mergeTranscriptText,
  transcribeAudioSegment,
  transcribeRecordingBlob,
} from "./transcribeRecording";

export type Recording = {
  id: string;
  blob: Blob;
  url: string;
  createdAt: Date;
  durationMs: number;
  sources: { tab: boolean; mic: boolean };
  transcriptionStatus: "idle" | "processing" | "done" | "error";
  transcript: string;
  transcriptionError: string | null;
  transcriptionSegments: number;
  processedDurationMs: number;
};

const SPEECH_RMS_THRESHOLD = 0.012;
const ENDPOINT_SILENCE_SECONDS = 3;
const TRAILING_SILENCE_SECONDS = 2;
const PRE_ROLL_SECONDS = 0.5;

const PCM_CAPTURE_WORKLET = `
class PcmCaptureProcessor extends AudioWorkletProcessor {
  process(inputs, outputs) {
    const input = inputs[0] && inputs[0][0];
    const output = outputs[0] && outputs[0][0];
    if (output) output.fill(0);
    if (input && input.length) {
      const copy = new Float32Array(input);
      this.port.postMessage(copy, [copy.buffer]);
    }
    return true;
  }
}
registerProcessor("pcm-capture", PcmCaptureProcessor);
`;

function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${rem.toString().padStart(2, "0")}`;
}

export { formatDuration };

function blobTypeFor(recorder: MediaRecorder | null) {
  return recorder?.mimeType || "audio/webm";
}

function rmsFloat(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (const sample of samples) sum += sample * sample;
  return Math.sqrt(sum / samples.length);
}

function totalSamples(frames: Float32Array[]) {
  return frames.reduce((sum, frame) => sum + frame.length, 0);
}

function flattenFrames(frames: Float32Array[], maxSamples = totalSamples(frames)) {
  const out = new Float32Array(Math.max(0, maxSamples));
  let offset = 0;
  for (const frame of frames) {
    if (offset >= out.length) break;
    const take = Math.min(frame.length, out.length - offset);
    out.set(frame.subarray(0, take), offset);
    offset += take;
  }
  return out;
}

export function useTabAudioRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [includeMicrophone, setIncludeMicrophone] = useState(true);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const displayStreamRef = useRef<MediaStream | null>(null);
  const sessionDisposeRef = useRef<(() => void) | null>(null);
  const sourcesRef = useRef<{ tab: boolean; mic: boolean }>({ tab: true, mic: false });
  const startedAtRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const captureContextRef = useRef<AudioContext | null>(null);
  const captureCleanupRef = useRef<(() => void) | null>(null);
  const currentRecordingIdRef = useRef<string | null>(null);
  const preRollFramesRef = useRef<Float32Array[]>([]);
  const currentFramesRef = useRef<Float32Array[]>([]);
  const activeUtteranceRef = useRef(false);
  const sampleCursorRef = useRef(0);
  const segmentStartSampleRef = useRef(0);
  const lastSpeechSampleRef = useRef(0);
  const pcmSampleRateRef = useRef(48_000);
  const segmentIndexRef = useRef(0);
  const transcriptionChainRef = useRef(Promise.resolve());
  const pendingByRecordingRef = useRef(new Map<string, number>());
  const finalizedRecordingIdsRef = useRef(new Set<string>());

  const updateRecording = useCallback((id: string, patch: Partial<Recording>) => {
    setRecordings((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const maybeMarkDone = useCallback((id: string) => {
    if (!finalizedRecordingIdsRef.current.has(id)) return;
    if ((pendingByRecordingRef.current.get(id) ?? 0) > 0) return;

    setRecordings((prev) =>
      prev.map((r) => {
        if (r.id !== id || r.transcriptionStatus === "error") return r;
        return {
          ...r,
          transcriptionStatus: "done",
          transcript: r.transcript || "(No speech detected.)",
        };
      }),
    );
  }, []);

  const enqueuePcmTranscription = useCallback(
    (recordingId: string, samples: Float32Array, sampleRate: number) => {
      const segment = preparePcmTranscriptionSegment(
        samples,
        sampleRate,
        segmentIndexRef.current++,
      );
      if (!segment) return;

      pendingByRecordingRef.current.set(
        recordingId,
        (pendingByRecordingRef.current.get(recordingId) ?? 0) + 1,
      );
      updateRecording(recordingId, {
        transcriptionStatus: "processing",
        transcriptionError: null,
      });

      transcriptionChainRef.current = transcriptionChainRef.current
        .then(async () => {
          const text = await transcribeAudioSegment(segment);
          setRecordings((prev) =>
            prev.map((r) =>
              r.id === recordingId
                ? {
                    ...r,
                    transcript: mergeTranscriptText(r.transcript, text),
                    transcriptionSegments: r.transcriptionSegments + 1,
                    processedDurationMs:
                      r.processedDurationMs + Math.round(segment.durationSeconds * 1000),
                  }
                : r,
            ),
          );
        })
        .catch((err) => {
          updateRecording(recordingId, {
            transcriptionStatus: "error",
            transcriptionError:
              err instanceof Error ? err.message : "Could not transcribe recording.",
          });
        })
        .finally(() => {
          const remaining = Math.max(
            0,
            (pendingByRecordingRef.current.get(recordingId) ?? 1) - 1,
          );
          if (remaining === 0) pendingByRecordingRef.current.delete(recordingId);
          else pendingByRecordingRef.current.set(recordingId, remaining);
          maybeMarkDone(recordingId);
        });
    },
    [maybeMarkDone, updateRecording],
  );

  const resetPcmUtterance = useCallback(() => {
    activeUtteranceRef.current = false;
    currentFramesRef.current = [];
    preRollFramesRef.current = [];
    segmentStartSampleRef.current = sampleCursorRef.current;
    lastSpeechSampleRef.current = sampleCursorRef.current;
  }, []);

  const flushPcmUtterance = useCallback(() => {
    const recordingId = currentRecordingIdRef.current;
    if (!recordingId || !activeUtteranceRef.current || currentFramesRef.current.length === 0) {
      resetPcmUtterance();
      return;
    }

    const sampleRate = pcmSampleRateRef.current;
    const keepUntil = lastSpeechSampleRef.current + TRAILING_SILENCE_SECONDS * sampleRate;
    const maxSamples = Math.max(
      0,
      Math.min(totalSamples(currentFramesRef.current), keepUntil - segmentStartSampleRef.current),
    );
    const samples = flattenFrames(currentFramesRef.current, maxSamples);
    resetPcmUtterance();

    if (samples.length >= sampleRate * 0.35) {
      enqueuePcmTranscription(recordingId, samples, sampleRate);
    }
  }, [enqueuePcmTranscription, resetPcmUtterance]);

  const pushPreRoll = useCallback((frame: Float32Array, sampleRate: number) => {
    const frames = preRollFramesRef.current;
    frames.push(frame);
    const maxSamples = Math.floor(PRE_ROLL_SECONDS * sampleRate);
    while (totalSamples(frames) > maxSamples && frames.length > 1) frames.shift();
  }, []);

  const handlePcmFrame = useCallback(
    (frame: Float32Array) => {
      const recordingId = currentRecordingIdRef.current;
      const sampleRate = pcmSampleRateRef.current;
      const frameStart = sampleCursorRef.current;
      const frameEnd = frameStart + frame.length;
      const isSpeech = rmsFloat(frame) >= SPEECH_RMS_THRESHOLD;

      if (!recordingId) {
        sampleCursorRef.current = frameEnd;
        return;
      }

      if (!activeUtteranceRef.current) {
        pushPreRoll(frame, sampleRate);
        if (isSpeech) {
          const preRollFrames = [...preRollFramesRef.current];
          activeUtteranceRef.current = true;
          currentFramesRef.current = preRollFrames;
          segmentStartSampleRef.current = frameEnd - totalSamples(preRollFrames);
          lastSpeechSampleRef.current = frameEnd;
          preRollFramesRef.current = [];
        }
      } else {
        currentFramesRef.current.push(frame);
        if (isSpeech) lastSpeechSampleRef.current = frameEnd;
        const silenceSamples = frameEnd - lastSpeechSampleRef.current;
        if (silenceSamples >= ENDPOINT_SILENCE_SECONDS * sampleRate) {
          flushPcmUtterance();
        }
      }

      sampleCursorRef.current = frameEnd;
    },
    [flushPcmUtterance, pushPreRoll],
  );

  const stopPcmCapture = useCallback(() => {
    captureCleanupRef.current?.();
    captureCleanupRef.current = null;
    void captureContextRef.current?.close();
    captureContextRef.current = null;
    resetPcmUtterance();
  }, [resetPcmUtterance]);

  const startPcmCapture = useCallback(
    async (stream: MediaStream) => {
      stopPcmCapture();
      const ctx = new AudioContext();
      pcmSampleRateRef.current = ctx.sampleRate;
      sampleCursorRef.current = 0;
      segmentIndexRef.current = 0;
      resetPcmUtterance();

      const moduleUrl = URL.createObjectURL(
        new Blob([PCM_CAPTURE_WORKLET], { type: "application/javascript" }),
      );
      try {
        await ctx.audioWorklet.addModule(moduleUrl);
      } finally {
        URL.revokeObjectURL(moduleUrl);
      }

      const source = ctx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(ctx, "pcm-capture", {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });
      const silent = ctx.createGain();
      silent.gain.value = 0;

      node.port.onmessage = (event: MessageEvent<Float32Array>) => {
        handlePcmFrame(event.data);
      };

      source.connect(node);
      node.connect(silent);
      silent.connect(ctx.destination);
      await ctx.resume();

      captureContextRef.current = ctx;
      captureCleanupRef.current = () => {
        node.port.onmessage = null;
        source.disconnect();
        node.disconnect();
        silent.disconnect();
      };
    },
    [handlePcmFrame, resetPcmUtterance, stopPcmCapture],
  );

  const cleanupSession = useCallback(() => {
    stopPcmCapture();
    sessionDisposeRef.current?.();
    sessionDisposeRef.current = null;
    displayStreamRef.current?.getTracks().forEach((t) => t.stop());
    displayStreamRef.current = null;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [stopPcmCapture]);

  const transcribeRecording = useCallback(
    async (recording: Recording) => {
      updateRecording(recording.id, {
        transcriptionStatus: "processing",
        transcriptionError: null,
        transcript: "",
        transcriptionSegments: 0,
        processedDurationMs: 0,
      });

      try {
        const result = await transcribeRecordingBlob(recording.blob);
        updateRecording(recording.id, {
          transcriptionStatus: "done",
          transcript: result.text || "(No speech detected.)",
          transcriptionSegments: result.segments.length,
          processedDurationMs: Math.round(
            result.segments.reduce((sum, s) => sum + s.durationSeconds * 1000, 0),
          ),
        });
      } catch (err) {
        updateRecording(recording.id, {
          transcriptionStatus: "error",
          transcriptionError:
            err instanceof Error ? err.message : "Could not transcribe recording.",
        });
      }
    },
    [updateRecording],
  );

  const startRecording = useCallback(async () => {
    setError(null);
    setWarning(null);
    transcriptionChainRef.current = Promise.resolve();

    try {
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });

      displayStreamRef.current = displayStream;

      const audioTracks = displayStream.getAudioTracks();
      if (audioTracks.length === 0) {
        displayStream.getTracks().forEach((t) => t.stop());
        displayStreamRef.current = null;
        setError(
          "No audio track. When sharing a tab, enable “Also share tab audio” in the Chrome picker.",
        );
        return;
      }

      displayStream.getVideoTracks().forEach((t) => t.stop());

      const tabAudioOnly = new MediaStream(audioTracks);
      const mixed = await mixTabAndMicrophone(tabAudioOnly, includeMicrophone);
      sessionDisposeRef.current = mixed.dispose;

      sourcesRef.current = { tab: true, mic: mixed.micIncluded };
      if (mixed.micError) setWarning(mixed.micError);

      const recordStream = mixed.stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "";
      const recorder = mime
        ? new MediaRecorder(recordStream, { mimeType: mime })
        : new MediaRecorder(recordStream);

      const recordingId = crypto.randomUUID();
      currentRecordingIdRef.current = recordingId;
      chunksRef.current = [];
      mediaRecorderRef.current = recorder;
      startedAtRef.current = Date.now();
      setElapsedMs(0);

      setRecordings((prev) => [
        {
          id: recordingId,
          blob: new Blob(),
          url: "",
          createdAt: new Date(),
          durationMs: 0,
          sources: { ...sourcesRef.current },
          transcriptionStatus: "processing",
          transcript: "",
          transcriptionError: null,
          transcriptionSegments: 0,
          processedDurationMs: 0,
        },
        ...prev,
      ]);

      timerRef.current = setInterval(() => {
        setElapsedMs(Date.now() - startedAtRef.current);
      }, 200);

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const durationMs = Date.now() - startedAtRef.current;
        flushPcmUtterance();
        const blob = new Blob(chunksRef.current, { type: blobTypeFor(recorder) });
        const url = URL.createObjectURL(blob);
        finalizedRecordingIdsRef.current.add(recordingId);
        updateRecording(recordingId, {
          blob,
          url,
          durationMs,
          sources: { ...sourcesRef.current },
        });
        maybeMarkDone(recordingId);
        cleanupSession();
        currentRecordingIdRef.current = null;
        setIsRecording(false);
        setElapsedMs(0);
      };

      const stopIfRecording = () => {
        if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
      };

      for (const track of tabAudioOnly.getTracks()) {
        track.addEventListener("ended", stopIfRecording, { once: true });
      }

      recorder.start(1000);
      await startPcmCapture(recordStream);
      setIsRecording(true);
    } catch (err) {
      const message =
        err instanceof DOMException && err.name === "NotAllowedError"
          ? "Permission denied or picker cancelled."
          : err instanceof Error
            ? err.message
            : "Could not start recording.";
      setError(message);
      cleanupSession();
      setIsRecording(false);
    }
  }, [cleanupSession, flushPcmUtterance, includeMicrophone, maybeMarkDone, startPcmCapture, updateRecording]);

  const stopRecording = useCallback(() => {
    flushPcmUtterance();
    if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
  }, [flushPcmUtterance]);

  const removeRecording = useCallback((id: string) => {
    setRecordings((prev) => {
      const item = prev.find((r) => r.id === id);
      if (item?.url) URL.revokeObjectURL(item.url);
      return prev.filter((r) => r.id !== id);
    });
  }, []);

  return {
    isRecording,
    recordings,
    error,
    warning,
    elapsedMs,
    includeMicrophone,
    setIncludeMicrophone,
    startRecording,
    stopRecording,
    removeRecording,
    transcribeRecording,
    clearError: () => setError(null),
    clearWarning: () => setWarning(null),
  };
}
